import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  BookOpen, Search, Loader2, Calculator, FileText, Scale, Plus, Save,
  TrendingUp, Lock, Download, AlertTriangle, Upload, Trash2, Landmark,
  Edit3, Database, Calendar, Check, X, ShieldCheck,
} from "lucide-react";
import ControlesTab from "@/components/compta/ControlesTab";
import TvaDecompteTab from "@/components/compta/TvaDecompteTab";
import { toast } from "sonner";
import api from "@/lib/api";
import ImportBancaireTab, { PiecesButton } from "@/pages/ImportBancaireTab";
import CompteSelect from "@/components/CompteSelect";

function fmtChf(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

const ORIGINE_LABELS = {
  facture: "Facture",
  depense: "Dépense",
  avoir: "Avoir",
  ecriture_bancaire: "Banque",
  compte_client: "Compte client",
  solde_initial: "Solde initial",
  autre: "Autre",
};
const ORIGINE_COLORS = {
  facture: "bg-emerald-50 text-emerald-700",
  depense: "bg-amber-50 text-amber-700",
  avoir: "bg-red-50 text-red-700",
  ecriture_bancaire: "bg-blue-50 text-blue-700",
  compte_client: "bg-violet-50 text-violet-700",
  solde_initial: "bg-indigo-50 text-indigo-700",
  autre: "bg-gray-50 text-gray-600",
};

// iter335 — Raccourcis de période pour la recherche dans le journal
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const PRESETS_PERIODE = [
  { key: "mois", label: "Ce mois", calc: () => {
    const n = new Date();
    return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(new Date(n.getFullYear(), n.getMonth() + 1, 0))];
  } },
  { key: "mois-dernier", label: "Mois dernier", calc: () => {
    const n = new Date();
    return [iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)), iso(new Date(n.getFullYear(), n.getMonth(), 0))];
  } },
  { key: "trimestre", label: "Ce trimestre", calc: () => {
    const n = new Date(); const t = Math.floor(n.getMonth() / 3) * 3;
    return [iso(new Date(n.getFullYear(), t, 1)), iso(new Date(n.getFullYear(), t + 3, 0))];
  } },
  { key: "annee", label: "Cette année", calc: () => {
    const y = new Date().getFullYear();
    return [`${y}-01-01`, `${y}-12-31`];
  } },
  { key: "annee-derniere", label: "Année dernière", calc: () => {
    const y = new Date().getFullYear() - 1;
    return [`${y}-01-01`, `${y}-12-31`];
  } },
];


function JournalTab() {
  const [q, setQ] = useState("");
  const [origine, setOrigine] = useState("all");
  const [compte, setCompte] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [plan, setPlan] = useState([]);
  const [codesTva, setCodesTva] = useState([]);
  const limit = 100;

  useEffect(() => {
    (async () => {
      const [{ data: pc }, { data: tv }] = await Promise.all([
        api.get("/journal/plan-comptes"),
        api.get("/journal/codes-tva"),
      ]);
      setPlan(pc);
      setCodesTva(tv);
    })();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (q) params.q = q;
      if (origine !== "all") params.origine = origine;
      if (compte) params.compte = compte;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: resp } = await api.get("/journal/ecritures", { params });
      setData(resp);
    } finally {
      setLoading(false);
    }
  }, [q, origine, compte, dateFrom, dateTo, page]);

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
  }, [fetchData]);

  return (
    <div data-testid="journal-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Référence, description..." className="pl-10" data-testid="ecriture-search" />
        </div>
        <Input value={compte} onChange={(e) => { setCompte(e.target.value); setPage(0); }} placeholder="Compte (1020...)" className="w-36" data-testid="ecriture-filter-compte" />
        <Select value={origine} onValueChange={(v) => { setOrigine(v); setPage(0); }}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Origine" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes origines</SelectItem>
            <SelectItem value="facture">Facture</SelectItem>
            <SelectItem value="ecriture_bancaire">Écriture bancaire</SelectItem>
            <SelectItem value="depense">Dépense</SelectItem>
            <SelectItem value="avoir">Avoir / crédit</SelectItem>
            <SelectItem value="compte_client">Compte client</SelectItem>
            <SelectItem value="manuel">Manuelle</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="ecriture-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle écriture
        </Button>
        <ImportBexioJournalBtn onDone={fetchData} />
        <DiagnosticOrphelinsBtn onCleanupDone={fetchData} />
      </div>

      {/* iter335 — Plage de dates + raccourcis + export de la sélection */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="journal-plage-dates">
        <span className="text-xs font-semibold text-gray-500 uppercase">Période</span>
        <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
               className="w-40" data-testid="ecriture-date-from" />
        <span className="text-gray-400 text-sm">→</span>
        <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
               className="w-40" data-testid="ecriture-date-to" />
        {PRESETS_PERIODE.map((p) => (
          <button key={p.label} type="button"
                  onClick={() => { const [a, b] = p.calc(); setDateFrom(a); setDateTo(b); setPage(0); }}
                  className="text-xs px-2.5 py-1 rounded-md border border-gray-200 text-gray-600 hover:border-[#52a675] hover:text-[#2f6b47] transition-colors"
                  data-testid={`preset-${p.key}`}>
            {p.label}
          </button>
        ))}
        {(dateFrom || dateTo) && (
          <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); setPage(0); }}
                  className="text-xs text-gray-500 underline hover:text-gray-800" data-testid="preset-reset">
            Toutes les dates
          </button>
        )}
        <div className="ml-auto">
          <TelechargerBtn filtres={{ q, origine, compte, dateFrom, dateTo }} total={data.total} />
        </div>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Référence</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Débit</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Crédit</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">TVA</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Montant</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">PJ</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-gray-400">Aucune écriture trouvée</td></tr>}
              {!loading && data.items.map((e) => (
                <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/50" data-testid={`ecriture-row-${e.id}`}>
                  <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2 text-xs text-gray-800">{e.reference}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="font-mono font-semibold text-gray-700">{e.debit_compte_numero}</span>
                    <span className="text-gray-600 ml-1">{e.debit_compte_libelle}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="font-mono font-semibold text-gray-700">{e.credit_compte_numero}</span>
                    <span className="text-gray-600 ml-1">{e.credit_compte_libelle}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{e.tva_code || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-sm">{fmtChf(e.montant)}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`${ORIGINE_COLORS[e.origine_categorie] || "bg-gray-50 text-gray-600"} text-[10px]`}>
                      {ORIGINE_LABELS[e.origine_categorie] || e.origine_categorie}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <PiecesButton parentId={e.id} pieces={e.pieces || []} kind="ecriture" onChange={fetchData} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <EditEcritureButton ecriture={e} onSaved={fetchData} />
                      <DeleteEcritureButton ecriture={e} onDeleted={fetchData} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.total > limit && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-xs text-gray-600">
            <div>{data.skip + 1} – {Math.min(data.skip + limit, data.total)} / {data.total}</div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Précédent</Button>
              <Button size="sm" variant="outline" disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          </div>
        )}
      </Card>
      <EcritureDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        plan={plan}
        codesTva={codesTva}
        onSaved={() => { setDialogOpen(false); fetchData(); }}
      />
    </div>
  );
}

/**
 * iter132.h — Édition d'une écriture (correction d'erreur de saisie).
 * Permet de changer les comptes débit/crédit, la description et la référence.
 * Refusé côté serveur si exercice clôturé ou facture liée payée/partielle.
 */
function EditEcritureButton({ ecriture, onSaved }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState([]);
  const [draft, setDraft] = useState({
    debit_numero: ecriture.debit_numero,
    credit_numero: ecriture.credit_numero,
    description: ecriture.description || "",
    reference: ecriture.reference,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || plan.length) return;
    api.get("/journal/plan-comptes").then((r) => setPlan(r.data || []));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (draft.debit_numero === draft.credit_numero) {
      toast.error("Débit et Crédit doivent être différents"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/journal/ecritures/${ecriture.id}`, {
        debit_numero: draft.debit_numero,
        credit_numero: draft.credit_numero,
        description: draft.description,
        reference: draft.reference,
      });
      toast.success("Écriture modifiée");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modification impossible");
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded transition"
        title="Modifier l'écriture"
        data-testid={`ecriture-edit-${ecriture.id}`}
      >
        ✏️
      </button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l&apos;écriture</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="bg-gray-50 rounded p-2 text-xs text-gray-600">
                <div><b>Montant :</b> {ecriture.montant?.toFixed(2)} CHF · <b>Date :</b> {ecriture.date}</div>
                <div className="text-gray-400 italic">Le montant et la date ne sont pas modifiables (utilisez une contre-écriture).</div>
              </div>
              <div>
                <Label className="text-xs">Référence</Label>
                <Input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} data-testid="ecriture-edit-reference" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Compte Débit</Label>
                  <select value={draft.debit_numero} onChange={(e) => setDraft({ ...draft, debit_numero: e.target.value })} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="ecriture-edit-debit">
                    {plan.map((c) => <option key={c.numero} value={c.numero}>{c.numero} — {c.libelle}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Compte Crédit</Label>
                  <select value={draft.credit_numero} onChange={(e) => setDraft({ ...draft, credit_numero: e.target.value })} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="ecriture-edit-credit">
                    {plan.map((c) => <option key={c.numero} value={c.numero}>{c.numero} — {c.libelle}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="ecriture-edit-description" />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Toute modification est tracée dans le journal d&apos;audit (CO art. 957).
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
              <Button onClick={save} disabled={saving} data-testid="ecriture-edit-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}


/**
 * Bouton de suppression d'une écriture comptable.
 * Refusé côté serveur si exercice clôturé ou facture liée payée/partielle.
 */
function DeleteEcritureButton({ ecriture, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    const label = `${ecriture.reference} · ${ecriture.montant?.toFixed(2)} CHF`;
    if (!window.confirm(`Supprimer l'écriture suivante ?\n\n${label}\n\nCette action est irréversible (mais auditée).\nUtilisez plutôt une contre-écriture si vous voulez conserver la traçabilité.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/journal/ecritures/${ecriture.id}`);
      toast.success("Écriture supprimée");
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    } finally {
      setDeleting(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleDelete}
      disabled={deleting}
      className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition disabled:opacity-30"
      title="Supprimer l'écriture"
      data-testid={`ecriture-delete-${ecriture.id}`}
    >
      {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
    </button>
  );
}

/**
 * Bouton admin d'import du journal complet depuis un export Bexio XLSX.
 * Déduplication automatique par (date + ref + débit + crédit + montant).
 */
function ImportBexioJournalBtn({ onDone }) {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const userRole = (() => {
    try { return JSON.parse(localStorage.getItem("cm_user") || "{}").role; }
    catch { return null; }
  })();
  if (userRole !== "admin") return null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Importer le journal "${file.name}" ?\n\nLes lignes déjà présentes en base seront ignorées automatiquement (déduplication par date + référence + comptes + montant).\nLes nouvelles seront marquées origine="bexio".`)) {
      e.target.value = "";
      return;
    }
    setImporting(true);
    setResult(null);
    setDialogOpen(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/journal/import-bexio-journal", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      toast.success(`Import OK : ${data.inserees} nouvelle(s), ${data.doublons_ignores} déjà en base`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <label className="inline-flex">
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFile}
          disabled={importing}
          className="hidden"
          data-testid="import-bexio-journal-input"
        />
        <Button
          variant="outline"
          asChild
          disabled={importing}
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
        >
          <span data-testid="import-bexio-journal-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importer journal
          </span>
        </Button>
      </label>

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import du journal Bexio</DialogTitle>
            <DialogDescription>
              Import avec déduplication automatique. Les écritures déjà en base ne sont jamais dupliquées.
            </DialogDescription>
          </DialogHeader>
          {importing && (
            <div className="flex items-center gap-3 py-6 justify-center text-sm text-gray-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              Import en cours... (cela peut prendre 1-2 minutes pour des gros fichiers)
            </div>
          )}
          {result && (
            <div className="space-y-2 text-sm py-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <div className="text-xs text-gray-500">Lignes Excel</div>
                  <div className="text-2xl font-bold">{result.lignes_excel}</div>
                </div>
                <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                  <div className="text-xs text-emerald-700">Nouvelles insérées</div>
                  <div className="text-2xl font-bold text-emerald-700">{result.inserees}</div>
                </div>
                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                  <div className="text-xs text-amber-700">Doublons ignorés</div>
                  <div className="text-2xl font-bold text-amber-700">{result.doublons_ignores}</div>
                </div>
                <div className={`p-3 rounded border ${result.erreurs > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                  <div className="text-xs text-gray-500">Erreurs</div>
                  <div className={`text-2xl font-bold ${result.erreurs > 0 ? "text-red-700" : "text-gray-700"}`}>{result.erreurs}</div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDialogOpen(false)} disabled={importing}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * iter336 — Téléchargement paramétrable : on choisit ce qu'on exporte
 * (écritures de la période filtrée, plan comptable, balance) et le format
 * (Excel ou CSV). Remplace l'ancien bouton « Migrer journal ».
 */
function TelechargerBtn({ filtres, total }) {
  const [open, setOpen] = useState(false);
  const [contenu, setContenu] = useState("ecritures");
  const [format, setFormat] = useState("xlsx");
  const [busy, setBusy] = useState(false);
  // iter344 — Contrôle automatique balance ↔ journal avant téléchargement
  const [controle, setControle] = useState(null);
  const [controleBusy, setControleBusy] = useState(false);

  const paramsFiltres = useCallback(() => {
    const params = {};
    if (filtres.dateFrom) params.date_from = filtres.dateFrom;
    if (filtres.dateTo) params.date_to = filtres.dateTo;
    if (filtres.q) params.q = filtres.q;
    if (filtres.origine && filtres.origine !== "all") params.origine = filtres.origine;
    if (filtres.compte) params.compte = filtres.compte;
    return params;
  }, [filtres]);

  useEffect(() => {
    if (!open) { setControle(null); return; }
    setControleBusy(true);
    api.get("/journal/controle-coherence", { params: paramsFiltres() })
      .then(({ data }) => setControle(data))
      .catch(() => setControle(null))
      .finally(() => setControleBusy(false));
  }, [open, paramsFiltres]);

  const telecharger = async () => {
    setBusy(true);
    try {
      const params = { contenu, format };
      if (contenu !== "plan") {
        if (filtres.dateFrom) params.date_from = filtres.dateFrom;
        if (filtres.dateTo) params.date_to = filtres.dateTo;
      }
      if (contenu === "ecritures") {
        if (filtres.q) params.q = filtres.q;
        if (filtres.origine && filtres.origine !== "all") params.origine = filtres.origine;
        if (filtres.compte) params.compte = filtres.compte;
      }
      const { data } = await api.get("/journal/ecritures/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${contenu === "plan" ? "plan_comptable" : contenu}_${filtres.dateFrom || "debut"}_${filtres.dateTo || "fin"}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Fichier téléchargé");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export impossible");
    } finally { setBusy(false); }
  };

  const CONTENUS = [
    { v: "ecritures", l: "Écritures du journal", d: total ? `${total} écriture(s) selon les filtres et la période ci-dessus` : "selon les filtres et la période ci-dessus" },
    { v: "balance", l: "Balance des comptes", d: "un total débit, crédit et solde par compte sur la période" },
    { v: "plan", l: "Plan comptable", d: "la liste des comptes, sans montants" },
  ];

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="journal-export-btn">
        <Download className="w-4 h-4 mr-2" />
        Télécharger {total ? `(${total})` : ""}
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="journal-export-dialog">
          <DialogHeader>
            <DialogTitle>Télécharger la comptabilité</DialogTitle>
            <DialogDescription>
              Choisissez ce que vous voulez extraire et dans quel format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {CONTENUS.map((c) => (
              <button key={c.v} type="button" onClick={() => setContenu(c.v)}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                        contenu === c.v ? "border-[#52a675] bg-[#f0f9f4]" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`export-contenu-${c.v}`}>
                <div className="text-sm font-semibold text-gray-900">{c.l}</div>
                <div className="text-xs text-gray-500">{c.d}</div>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Format</span>
            {[["xlsx", "Excel (.xlsx)"], ["csv", "CSV (.csv)"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFormat(v)}
                      className={`text-xs px-2.5 py-1 rounded-md border ${
                        format === v ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200"}`}
                      data-testid={`export-format-${v}`}>
                {l}
              </button>
            ))}
          </div>

          {contenu !== "plan" && (
            <p className="text-xs text-gray-500">
              Période : {filtres.dateFrom || "depuis l'origine"} → {filtres.dateTo || "aujourd'hui"}
            </p>
          )}

          {/* iter344 — Contrôle de cohérence balance ↔ journal */}
          {contenu === "ecritures" && (
            <div data-testid="export-controle">
              {controleBusy && (
                <div className="text-xs text-gray-500 flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Contrôle de cohérence en cours…
                </div>
              )}
              {!controleBusy && controle?.coherent && (
                <div className="text-xs bg-[#f0f9f4] border border-[#52a675]/40 text-[#2f6b4a] rounded px-3 py-2"
                     data-testid="export-controle-ok">
                  ✓ Export conforme à la balance : {controle.nb_ecritures.toLocaleString("fr-CH")} écriture(s),
                  total CHF {fmtChf(controle.total_export)} = total débit et crédit de la balance.
                </div>
              )}
              {!controleBusy && controle && !controle.coherent && (
                <div className="text-xs bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 space-y-1"
                     data-testid="export-controle-ecart">
                  <div className="font-semibold">
                    ⚠ Écart détecté entre le journal exporté et la balance :
                    CHF {fmtChf(Math.abs(controle.ecart_total || 0))}
                    {controle.doublons_detectes > 0 && ` · ${controle.doublons_detectes} doublon(s)`}
                  </div>
                  {controle.comptes_ecart?.slice(0, 8).map((c) => (
                    <div key={c.numero} className="font-mono">
                      {c.numero} {c.libelle ? `– ${c.libelle}` : ""} : débit {fmtChf(c.ecart_debit)} ·
                      crédit {fmtChf(c.ecart_credit)}
                    </div>
                  ))}
                  {controle.nb_comptes_ecart > 8 && (
                    <div>… et {controle.nb_comptes_ecart - 8} autre(s) compte(s)</div>
                  )}
                  {controle.tronque && <div>Le volume dépasse la limite d&apos;export : découpez par période.</div>}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={telecharger} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]"
                    data-testid="export-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * Bouton admin/compta pour définir un **solde de référence** sur n'importe quel
 * compte (typiquement 1020 Banque Raiffeisen) à une date donnée. Crée
 * automatiquement une écriture d'ajustement contre 9100 - Bilan d'ouverture
 * pour faire correspondre le solde comptable au relevé réel.
 *
 * Idempotent : refuse si un solde initial existe déjà pour ce couple
 * (compte, date) ; affiche les soldes existants avec un bouton supprimer.
 */
function SoldeReferenceBtn({ plan, onDone }) {
  const [open, setOpen] = useState(false);
  const [compte, setCompte] = useState("1020");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [montantCible, setMontantCible] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPreview = useCallback(async () => {
    if (!compte || !date) { setPreview(null); return; }
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/journal/solde-compte", { params: { compte, date } });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      toast.error(e.response?.data?.detail || "Impossible de calculer le solde");
    } finally { setLoadingPreview(false); }
  }, [compte, date]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchPreview, 200);
    return () => clearTimeout(t);
  }, [open, fetchPreview]);

  const userRole = (() => {
    try { return JSON.parse(localStorage.getItem("cm_user") || "{}").role; }
    catch { return null; }
  })();
  if (userRole !== "admin" && userRole !== "compta") return null;

  // Comptes monétaires (1xxx : actifs liquides, comptes bancaires, caisses)
  const comptesEligibles = (plan || []).filter(
    (c) => /^(10|11)\d{2}$/.test(c.numero || ""),
  );

  const cibleNum = parseFloat((montantCible || "").replace(",", ".")) || 0;
  const ajustement = preview ? +(cibleNum - preview.solde_naturel).toFixed(2) : null;
  const aDejaUnSolde = (preview?.soldes_initiaux_existants || []).some((s) => s.date === date);

  const handleSubmit = async () => {
    if (!compte || !date) { toast.error("Compte et date obligatoires"); return; }
    if (Number.isNaN(parseFloat(montantCible)) || montantCible === "") {
      toast.error("Montant cible obligatoire (peut être 0)"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/journal/solde-initial", {
        compte_numero: compte,
        date,
        montant_cible: cibleNum,
      });
      if (data.already_aligned) {
        toast.success("Le solde est déjà aligné, aucune écriture créée");
      } else {
        toast.success(`Écriture créée : ajustement ${data.ajustement >= 0 ? "+" : ""}${data.ajustement.toFixed(2)} CHF`);
      }
      await fetchPreview();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
    } finally { setSaving(false); }
  };

  const handleDeleteSolde = async (sid) => {
    if (!window.confirm("Supprimer cette écriture de solde initial ?\n\nLe solde du compte reviendra à sa valeur naturelle.")) return;
    try {
      await api.delete(`/journal/ecritures/${sid}`);
      toast.success("Solde initial supprimé");
      await fetchPreview();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
        data-testid="solde-reference-btn"
      >
        <Landmark className="w-4 h-4 mr-2" />
        Solde de référence
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="solde-reference-dialog">
          <DialogHeader>
            <DialogTitle>Solde de référence bancaire</DialogTitle>
            <DialogDescription>
              Aligne le solde comptable d'un compte (banque, caisse…) sur un relevé réel
              à une date donnée. Crée automatiquement une écriture d'ajustement contre
              le compte <b>9100 — Bilan d'ouverture</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Compte cible</Label>
                <CompteSelect
                  value={compte}
                  onValueChange={setCompte}
                  options={comptesEligibles.map((c) => ({ value: c.numero, label: `${c.numero} — ${c.libelle}` }))}
                  data-testid="solde-ref-compte"
                />
              </div>
              <div>
                <Label className="text-xs">Date du relevé</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="solde-ref-date"
                />
              </div>
              <div>
                <Label className="text-xs">Solde réel à cette date (CHF)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={montantCible}
                  onChange={(e) => setMontantCible(e.target.value)}
                  placeholder="6359.53"
                  data-testid="solde-ref-montant"
                />
              </div>
            </div>

            {loadingPreview && (
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Calcul du solde…
              </div>
            )}

            {preview && !loadingPreview && (
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <div className="text-[11px] text-gray-500 uppercase">Solde courant</div>
                  <div className={`text-xl font-bold ${preview.solde_naturel < 0 ? "text-red-700" : "text-gray-800"}`}>
                    {fmtChf(preview.solde_naturel)} CHF
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    D {fmtChf(preview.debit)} · C {fmtChf(preview.credit)}
                  </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                  <div className="text-[11px] text-emerald-700 uppercase">Cible</div>
                  <div className="text-xl font-bold text-emerald-800">
                    {montantCible ? `${fmtChf(cibleNum)} CHF` : "—"}
                  </div>
                </div>
                <div className={`border-2 rounded p-3 ${ajustement === null || montantCible === "" ? "bg-gray-50 border-gray-200" : Math.abs(ajustement) < 0.01 ? "bg-emerald-50 border-emerald-300" : "bg-indigo-50 border-indigo-300"}`}>
                  <div className="text-[11px] uppercase text-indigo-700">Ajustement</div>
                  <div className="text-xl font-bold">
                    {montantCible === "" ? "—" :
                      Math.abs(ajustement) < 0.01 ? <span className="text-emerald-700">Déjà aligné ✓</span> :
                      <span className={ajustement >= 0 ? "text-indigo-800" : "text-red-700"}>
                        {ajustement >= 0 ? "+" : ""}{fmtChf(ajustement)} CHF
                      </span>
                    }
                  </div>
                  {montantCible !== "" && Math.abs(ajustement) >= 0.01 && (
                    <div className="text-[10px] text-indigo-700 mt-1">
                      {ajustement > 0 ? `Débit ${compte} / Crédit 9100` : `Débit 9100 / Crédit ${compte}`}
                    </div>
                  )}
                </div>
              </div>
            )}

            {aDejaUnSolde && (
              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900">
                ⚠️ Un solde initial existe déjà pour ce compte à cette date. Supprimez-le d'abord pour le redéfinir.
              </div>
            )}

            {preview?.soldes_initiaux_existants?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-1">Soldes initiaux existants sur ce compte :</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {preview.soldes_initiaux_existants.map((s) => {
                    const sens = s.debit_compte_numero === compte ? "+" : "−";
                    return (
                      <div key={s.id} className="flex items-center justify-between text-xs bg-indigo-50/50 border border-indigo-100 rounded px-2 py-1">
                        <span className="font-mono text-indigo-800">
                          {fmtDate(s.date)} · {sens}{fmtChf(s.montant)} CHF
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteSolde(s.id)}
                          className="text-gray-400 hover:text-red-600 p-1 rounded"
                          title="Supprimer ce solde initial"
                          data-testid={`solde-ref-delete-${s.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="solde-ref-cancel">
              Fermer
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !preview || montantCible === "" || aDejaUnSolde}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="solde-ref-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {ajustement !== null && Math.abs(ajustement) < 0.01 ? "Déjà aligné" : "Créer l'écriture"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * Bouton admin de diagnostic + nettoyage des écritures orphelines
 * (écritures avec facture_id pointant vers une facture inexistante).
 */
function DiagnosticOrphelinsBtn({ onCleanupDone }) {
  const [diagOpen, setDiagOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const userRole = (() => {
    try { return JSON.parse(localStorage.getItem("cm_user") || "{}").role; }
    catch { return null; }
  })();
  if (userRole !== "admin") return null;

  const runDiag = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/diagnostic-orphelins");
      setDiag(data);
      setDiagOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur diagnostic");
    } finally { setLoading(false); }
  };

  const runCleanup = async () => {
    if (!window.confirm(`Supprimer définitivement ${diag.nb_orphelins} écriture(s) orpheline(s) ?\n\nUn backup audit sera conservé. Cette action retire les écritures dont la facture liée n'existe plus en base.`)) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/journal/cleanup-orphelins");
      toast.success(`${data.nb_supprimees} écriture(s) supprimée(s)`);
      setDiagOpen(false);
      onCleanupDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur cleanup");
    } finally { setCleaning(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={runDiag}
        disabled={loading}
        className="border-amber-300 text-amber-700 hover:bg-amber-50"
        data-testid="diagnostic-orphelins-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
        Diagnostic intégrité
      </Button>

      <Dialog open={diagOpen} onOpenChange={(o) => !o && setDiagOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Diagnostic intégrité comptable</DialogTitle>
            <DialogDescription>
              Détecte les anomalies entre la collection des factures et le journal comptable
              (résidus de tests, suppressions manuelles non purgées, etc.).
            </DialogDescription>
          </DialogHeader>

          {diag && (
            <div className="space-y-4 py-2">
              <div className={`p-3 rounded-lg border-2 ${diag.nb_orphelins > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}>
                <div className="font-semibold">
                  {diag.nb_orphelins === 0 ? "✅ Aucune écriture orpheline" : `⚠️ ${diag.nb_orphelins} factures avec écritures orphelines`}
                </div>
                {diag.nb_orphelins > 0 && (
                  <div className="text-sm text-amber-900 mt-1">
                    Écritures rattachées à une facture qui n'existe plus en base (risque de double-comptabilisation si le n° est ré-attribué).
                  </div>
                )}
                {diag.orphelins?.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto text-xs space-y-1">
                    {diag.orphelins.map((o) => (
                      <div key={o.facture_id} className="font-mono">
                        • {o.references?.[0] || o.facture_id.slice(0, 8)} — {o.nb_ecritures} écriture(s), total {o.montant_total} CHF
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={`p-3 rounded-lg border-2 ${diag.nb_doublons_reference > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`}>
                <div className="font-semibold">
                  {diag.nb_doublons_reference === 0 ? "✅ Aucun doublon de référence" : `🚨 ${diag.nb_doublons_reference} référence(s) en doublon`}
                </div>
                {diag.nb_doublons_reference > 0 && (
                  <div className="text-sm text-red-900 mt-1">
                    Plusieurs factures (id différents) partagent le même numéro RE-XXXXX dans le journal. C'est la cause directe de la comptabilisation en double.
                  </div>
                )}
                {diag.doublons_reference?.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto text-xs space-y-1">
                    {diag.doublons_reference.map((d) => (
                      <div key={d.reference} className="font-mono">• {d.reference} ({d.nb_factures} facture_id distincts)</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDiagOpen(false)}>Fermer</Button>
            {diag?.nb_orphelins > 0 && (
              <Button
                onClick={runCleanup}
                disabled={cleaning}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="cleanup-orphelins-btn"
              >
                {cleaning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Nettoyer {diag.nb_orphelins} entrée(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EcritureDialog({ open, onClose, plan, codesTva, onSaved }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [debit, setDebit] = useState("");
  const [credit, setCredit] = useState("");
  const [montant, setMontant] = useState("");
  const [tva, setTva] = useState("none");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(new Date().toISOString().slice(0, 10));
      setReference("");
      setDescription("");
      setDebit("");
      setCredit("");
      setMontant("");
      setTva("none");
    }
  }, [open]);

  const submit = async () => {
    if (!reference.trim()) { toast.error("Référence obligatoire"); return; }
    if (!debit || !credit) { toast.error("Comptes débit et crédit obligatoires"); return; }
    const m = parseFloat(montant);
    if (!m || m <= 0) { toast.error("Montant invalide"); return; }
    setSubmitting(true);
    try {
      await api.post("/journal/ecritures", {
        date,
        reference,
        debit_numero: debit,
        credit_numero: credit,
        montant: m,
        description: description || null,
        tva_code: tva === "none" ? null : tva,
      });
      toast.success("Écriture enregistrée");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nouvelle écriture comptable</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 py-2">
          <div>
            <Label>Date *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ecr-date" />
          </div>
          <div>
            <Label>Référence *</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ex : Facture fournisseur 2025-128" data-testid="ecr-reference" />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Libellé interne (optionnel)" data-testid="ecr-description" />
          </div>
          <div>
            <Label>Débit *</Label>
            <CompteSelect
              value={debit}
              onValueChange={setDebit}
              options={plan.map((c) => ({ value: c.numero, label: `${c.numero} – ${c.libelle}` }))}
              placeholder="Compte..."
              data-testid="ecr-debit"
            />
          </div>
          <div>
            <Label>Crédit *</Label>
            <CompteSelect
              value={credit}
              onValueChange={setCredit}
              options={plan.map((c) => ({ value: c.numero, label: `${c.numero} – ${c.libelle}` }))}
              placeholder="Compte..."
              data-testid="ecr-credit"
            />
          </div>
          <div>
            <Label>Montant (CHF) *</Label>
            <Input type="number" step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} data-testid="ecr-montant" />
          </div>
          <div>
            <Label>Code TVA</Label>
            <Select value={tva} onValueChange={setTva}>
              <SelectTrigger data-testid="ecr-tva"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {codesTva.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code} ({(c.taux || 0).toFixed(2)}%)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="ecr-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BalanceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/journal/balance");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!data) return null;
  const equilibre = Math.abs((data.total_debit || 0) - (data.total_credit || 0)) < 0.5;
  return (
    <div data-testid="balance-tab">
      <Card className={`p-4 mb-4 border-2 ${equilibre ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30"}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Scale className={`w-5 h-5 ${equilibre ? "text-emerald-600" : "text-red-600"}`} />
            <span className={`font-semibold ${equilibre ? "text-emerald-900" : "text-red-900"}`}>
              {equilibre ? "Balance équilibrée" : `Déséquilibre : ${Math.abs(data.total_debit - data.total_credit).toFixed(2)} CHF`}
            </span>
          </div>
          <div className="text-sm flex gap-4 font-mono">
            <div>Débit : <span className="font-bold">{fmtChf(data.total_debit)}</span></div>
            <div>Crédit : <span className="font-bold">{fmtChf(data.total_credit)}</span></div>
          </div>
        </div>
      </Card>
      <Card className="border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">N°</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Compte</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Débit</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Crédit</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Solde</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.numero} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{r.numero}</td>
                  <td className="px-3 py-2 text-gray-700">{r.libelle}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.debit > 0 ? fmtChf(r.debit) : <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.credit > 0 ? fmtChf(r.credit) : <span className="text-gray-300">—</span>}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${r.solde >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtChf(r.solde)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PlanComptesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // {numero, libelle, type}
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState({ numero: "", libelle: "", type: "charge" });

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/plan-comptes");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const TYPE_LABELS = { actif: "Actif", passif: "Passif", charge: "Charge", produit: "Produit", resultat: "Résultat", autre: "Autre" };
  const TYPE_COLORS = {
    actif: "bg-blue-50 text-blue-700 border-blue-200",
    passif: "bg-orange-50 text-orange-700 border-orange-200",
    charge: "bg-red-50 text-red-700 border-red-200",
    produit: "bg-emerald-50 text-emerald-700 border-emerald-200",
    resultat: "bg-violet-50 text-violet-700 border-violet-200",
    autre: "bg-gray-50 text-gray-700 border-gray-200",
  };

  const saveEdit = async () => {
    if (!editing || !editing.libelle?.trim()) {
      toast.error("Libellé requis"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/journal/plan-comptes/${editing.numero}`, {
        libelle: editing.libelle.trim(),
        type: editing.type,
      });
      toast.success(`Compte ${editing.numero} mis à jour`);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const addCompte = async () => {
    if (!newDraft.numero.trim() || !newDraft.libelle.trim()) {
      toast.error("Numéro et libellé requis"); return;
    }
    setSaving(true);
    try {
      await api.post("/journal/plan-comptes", {
        numero: newDraft.numero.trim(),
        libelle: newDraft.libelle.trim(),
        type: newDraft.type,
      });
      toast.success(`Compte ${newDraft.numero} créé`);
      setAdding(false);
      setNewDraft({ numero: "", libelle: "", type: "charge" });
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const deleteCompte = async (numero) => {
    if (!window.confirm(`Supprimer le compte ${numero} ?\nRefusé si référencé par une écriture.`)) return;
    try {
      await api.delete(`/journal/plan-comptes/${numero}`);
      toast.success(`Compte ${numero} supprimé`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <Card className="border-gray-200 overflow-hidden" data-testid="plan-comptes-tab">
      <div className="flex items-center justify-between p-3 border-b border-gray-100">
        <div className="text-sm font-semibold text-gray-700">{items.length} compte(s)</div>
        <Button size="sm" onClick={() => setAdding((a) => !a)} data-testid="plan-comptes-toggle-add">
          <Plus className="w-4 h-4 mr-1" /> {adding ? "Annuler" : "Ajouter un compte"}
        </Button>
      </div>
      {adding && (
        <div className="p-3 bg-emerald-50/50 border-b border-emerald-100 grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="plan-comptes-add-form">
          <Input value={newDraft.numero} onChange={(e) => setNewDraft({ ...newDraft, numero: e.target.value })} placeholder="N° (ex: 5910)" className="font-mono" data-testid="plan-comptes-new-numero" />
          <Input value={newDraft.libelle} onChange={(e) => setNewDraft({ ...newDraft, libelle: e.target.value })} placeholder="Libellé du compte" data-testid="plan-comptes-new-libelle" />
          <select value={newDraft.type} onChange={(e) => setNewDraft({ ...newDraft, type: e.target.value })} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="plan-comptes-new-type">
            <option value="actif">Actif</option><option value="passif">Passif</option>
            <option value="charge">Charge</option><option value="produit">Produit</option>
            <option value="resultat">Résultat</option><option value="autre">Autre</option>
          </select>
          <Button size="sm" onClick={addCompte} disabled={saving} data-testid="plan-comptes-create">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
          </Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">N°</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Libellé</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const isEdit = editing?.numero === c.numero;
              return (
                <tr key={c.numero} className="border-t border-gray-100" data-testid={`compte-${c.numero}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{c.numero}</td>
                  <td className="px-3 py-2 text-gray-800">
                    {isEdit ? (
                      <Input value={editing.libelle} onChange={(e) => setEditing({ ...editing, libelle: e.target.value })} className="h-8 text-sm" data-testid={`compte-${c.numero}-libelle-edit`} />
                    ) : c.libelle}
                  </td>
                  <td className="px-3 py-2">
                    {isEdit ? (
                      <select value={editing.type || ""} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`compte-${c.numero}-type-edit`}>
                        <option value="actif">Actif</option><option value="passif">Passif</option>
                        <option value="charge">Charge</option><option value="produit">Produit</option>
                        <option value="resultat">Résultat</option><option value="autre">Autre</option>
                      </select>
                    ) : (
                      <Badge variant="outline" className={TYPE_COLORS[c.type]}>{TYPE_LABELS[c.type]}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isEdit ? (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Annuler</Button>
                        <Button size="sm" onClick={saveEdit} disabled={saving} data-testid={`compte-${c.numero}-save`}>
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Enregistrer"}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ numero: c.numero, libelle: c.libelle, type: c.type || "charge" })} data-testid={`compte-${c.numero}-edit`}>
                          ✏️
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCompte(c.numero)} data-testid={`compte-${c.numero}-delete`} title="Supprimer (si non utilisé)">
                          🗑️
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ClotureTab({ role }) {
  const [exercices, setExercices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const canClose = role === "admin" || role === "compta";

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/exercices");
      setExercices(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const cloturer = async (exid) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/journal/exercices/${exid}/cloturer`);
      toast.success(`Exercice ${data.annee} clôturé : ${data.ecritures_verrouillees} écritures + ${data.factures_verrouillees} factures verrouillées.`);
      setConfirming(null);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  return (
    <div data-testid="cloture-tab" className="space-y-4">
      <Card className="p-4 border-amber-200 bg-amber-50/50">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">Clôture définitive (art. 957 CO)</p>
            <p className="text-xs">
              La clôture d'un exercice rend <strong>toutes les écritures comptables et factures de la période immutables</strong>.
              Plus aucune saisie, modification ou suppression ne sera possible dans cette période, conformément à l'obligation
              d'immutabilité de la comptabilité suisse. Cette action est <strong>irréversible</strong>.
            </p>
          </div>
        </div>
      </Card>

      <Card className="border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Année</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Période</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Clôture</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {exercices.map((ex) => (
              <tr key={ex.id} className="border-t border-gray-100" data-testid={`exercice-${ex.annee}`}>
                <td className="px-3 py-2 font-bold text-gray-900">{ex.annee}</td>
                <td className="px-3 py-2 text-gray-600">{ex.date_debut} → {ex.date_fin}</td>
                <td className="px-3 py-2">
                  {ex.definitif ? (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><Lock className="w-3 h-3 mr-1" /> Clôturé</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Ouvert</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {ex.definitif ? `${(ex.cloture_at || "").slice(0, 10)} par ${ex.cloture_by_nom || "—"}` : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  {!ex.definitif && canClose && (
                    <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => setConfirming(ex)} data-testid={`cloture-btn-${ex.annee}`}>
                      <Lock className="w-3.5 h-3.5 mr-1.5" /> Clôturer
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!confirming} onOpenChange={(o) => !o && setConfirming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clôturer définitivement l'exercice {confirming?.annee} ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-red-50 border-2 border-red-200 rounded text-sm text-red-900">
              <p className="font-semibold mb-1">⚠️ Action irréversible</p>
              <p className="text-xs">Toutes les écritures et factures du {confirming?.date_debut} au {confirming?.date_fin} seront verrouillées. Vous ne pourrez plus créer, modifier ni supprimer de pièces dans cette période.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>Annuler</Button>
            <Button onClick={() => cloturer(confirming.id)} disabled={submitting} className="bg-red-600 hover:bg-red-700" data-testid="cloture-confirm">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Oui, clôturer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BilanTab() {
  const today = new Date();
  const [date, setDate] = useState(today.toISOString().slice(0, 10));
  const [debut, setDebut] = useState(`${today.getFullYear()}-01-01`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState([]);
  useEffect(() => {
    api.get("/journal/plan-comptes").then(({ data: pc }) => setPlan(pc)).catch(() => {});
  }, []);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/bilan", { params: { date_to: date, date_from: debut } });
      setData(data);
    } finally { setLoading(false); }
  }, [date, debut]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="bilan-tab" className="space-y-4">
      <Card className="p-4 border-gray-200 flex items-center gap-3 flex-wrap">
        <Label className="text-xs uppercase text-gray-500">Bilan au :</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="bilan-date" />
        <Label className="text-xs uppercase text-gray-500">Exercice depuis :</Label>
        <Input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} className="w-40" data-testid="bilan-exercice-debut" />
        <span className="text-xs text-gray-400 italic">(soldes cumulés depuis l&apos;origine du journal)</span>
        {/* iter336 — Déplacé depuis l'onglet Journal : sa place est auprès des soldes de comptes */}
        <div className="ml-auto"><SoldeReferenceBtn plan={plan} onDone={fetchData} /></div>
      </Card>
      {loading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!loading && data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="border-blue-200 overflow-hidden">
            <div className="p-3 bg-blue-50 border-b border-blue-200 font-semibold text-blue-900 flex items-center justify-between">
              <span>ACTIF</span>
              <span className="font-mono">{fmt(data.total_actif)} CHF</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.actifs.map((a) => (
                  <tr key={a.numero} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 font-mono text-xs font-semibold w-16">{a.numero}</td>
                    <td className="px-3 py-1.5 text-gray-700">
                      {a.libelle}
                      {a.hors_plan && <HorsPlanBadge />}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{fmt(a.solde)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card className="border-orange-200 overflow-hidden">
            <div className="p-3 bg-orange-50 border-b border-orange-200 font-semibold text-orange-900 flex items-center justify-between">
              <span>PASSIF</span>
              <span className="font-mono">{fmt(data.total_passif + data.resultat_reporte + data.resultat_exercice)} CHF</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.passifs.map((p) => (
                  <tr key={p.numero} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 font-mono text-xs font-semibold w-16">{p.numero}</td>
                    <td className="px-3 py-1.5 text-gray-700">
                      {p.libelle}
                      {p.hors_plan && <HorsPlanBadge />}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{fmt(p.solde)}</td>
                  </tr>
                ))}
                <tr className="border-t border-gray-100">
                  <td className="px-3 py-1.5"></td>
                  <td className="px-3 py-1.5 text-gray-700 italic">
                    Résultat reporté (avant le {debut.split("-").reverse().join(".")})
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono text-xs ${data.resultat_reporte >= 0 ? "text-gray-700" : "text-red-700"}`} data-testid="bilan-resultat-reporte">
                    {fmt(data.resultat_reporte)}
                  </td>
                </tr>
                <tr className="border-t-2 border-orange-200 bg-orange-50/50">
                  <td className="px-3 py-1.5"></td>
                  <td className="px-3 py-1.5 text-gray-700 italic">
                    Résultat de l&apos;exercice ({debut.split("-").reverse().join(".")} → {date.split("-").reverse().join(".")})
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono text-xs font-bold ${data.resultat_exercice >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="bilan-resultat-exercice">
                    {fmt(data.resultat_exercice)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
          <div className="lg:col-span-2 space-y-3">
            {data.comptes_hors_classe?.length > 0 && (
              <Card className="p-3 bg-red-50 border-red-200" data-testid="bilan-hors-classe">
                <div className="text-sm font-semibold text-red-900">
                  {data.comptes_hors_classe.length} compte(s) hors classe 1-9 — absents du bilan et du résultat
                </div>
                <div className="text-xs text-red-800 mt-1 font-mono">
                  {data.comptes_hors_classe.map((c) => `${c.numero} (${fmt(c.solde)})`).join(" · ")}
                </div>
              </Card>
            )}
            <Card className="p-3 bg-gray-50 border-gray-200" data-testid="bilan-controle">
              <div className="text-sm text-gray-700 flex items-center justify-between flex-wrap gap-2">
                <span>
                  Contrôle structurel : Actif − (Passif + Report + Résultat) ={" "}
                  <span className="font-mono">{fmt(data.controle_structurel)}</span>
                </span>
                <span className="text-xs text-gray-500 italic">
                  Ce contrôle vaut toujours 0 (débit = crédit dans chaque écriture) — les vraies
                  anomalies sont dans l&apos;onglet « Contrôles ».
                </span>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// iter296 — Le libellé affiché vient du plan comptable. Si le compte n'y figure pas,
// on le signale pour que l'admin l'ajoute (Journal → Plan comptable).
const HorsPlanBadge = () => (
  <span
    className="ml-1.5 align-middle text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
    title="Ce compte n'existe pas dans le plan comptable : ajoute-le pour lui donner un nom officiel."
    data-testid="compte-hors-plan"
  >
    hors plan
  </span>
);

function CompteResultatTab() {
  const today = new Date();
  const [from, setFrom] = useState(`${today.getFullYear()}-01-01`);
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/compte-resultat", { params: { date_from: from, date_to: to } });
      setData(data);
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="pnl-tab" className="space-y-4">
      <Card className="p-4 border-gray-200 flex items-center gap-2 flex-wrap">
        <Label className="text-xs uppercase text-gray-500">Période :</Label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="pnl-from" />
        <span className="text-gray-400">→</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="pnl-to" />
      </Card>
      {loading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!loading && data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="border-emerald-200 overflow-hidden">
            <div className="p-3 bg-emerald-50 border-b border-emerald-200 font-semibold text-emerald-900 flex items-center justify-between">
              <span>PRODUITS</span>
              <span className="font-mono">{fmt(data.total_produits)} CHF</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.produits.map((p) => (
                  <tr key={p.numero} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 font-mono text-xs font-semibold w-16">{p.numero}</td>
                    <td className="px-3 py-1.5 text-gray-700">
                      {p.libelle}
                      {p.hors_plan && <HorsPlanBadge />}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{fmt(p.solde)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card className="border-red-200 overflow-hidden">
            <div className="p-3 bg-red-50 border-b border-red-200 font-semibold text-red-900 flex items-center justify-between">
              <span>CHARGES</span>
              <span className="font-mono">{fmt(data.total_charges)} CHF</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.charges.map((c) => (
                  <tr key={c.numero} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 font-mono text-xs font-semibold w-16">{c.numero}</td>
                    <td className="px-3 py-1.5 text-gray-700">
                      {c.libelle}
                      {c.hors_plan && <HorsPlanBadge />}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{fmt(c.solde)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <div className="lg:col-span-2">
            <Card className={`p-4 border-2 ${data.resultat_net >= 0 ? "border-emerald-300 bg-emerald-50/50" : "border-red-300 bg-red-50/50"}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-base font-bold">
                  {data.resultat_net >= 0 ? "📈 Bénéfice de la période" : "📉 Perte de la période"}
                </span>
                <span className={`font-mono text-2xl font-bold ${data.resultat_net >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {fmt(data.resultat_net)} CHF
                </span>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// OD Périodiques Tab (V2 transitoire, iter31)
// ============================================================
function OdPeriodiquesTab({ role }) {
  const [globalActif, setGlobalActif] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateLimite, setDateLimite] = useState(() => {
    const d = new Date();
    d.setDate(0);  // Dernier jour du mois précédent
    return d.toISOString().slice(0, 10);
  });
  const [statutFilter, setStatutFilter] = useState("");

  const canEdit = role === "admin" || role === "compta";

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, list] = await Promise.all([
        api.get("/config/transitoire"),
        api.get("/journal/od-periodiques", { params: statutFilter ? { statut: statutFilter } : {} }),
      ]);
      setGlobalActif(!!cfg.data?.actif);
      setItems(list.data.items || []);
    } catch {
      toast.error("Erreur chargement OD périodiques");
    } finally {
      setLoading(false);
    }
  }, [statutFilter]);

  useEffect(() => { reload(); }, [reload]);

  const toggleGlobal = async () => {
    if (!canEdit) return;
    const next = !globalActif;
    try {
      await api.put("/config/transitoire", { actif: next });
      setGlobalActif(next);
      toast.success(next
        ? "Comptabilité V2 transitoire activée globalement"
        : "Comptabilité V2 transitoire désactivée globalement");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const materialiser = async () => {
    if (!canEdit) return;
    if (!window.confirm(
      `Matérialiser toutes les OD pending dont la date ≤ ${dateLimite} ?\n\n` +
      `Chaque OD devient une écriture comptable réelle : Débit compte charge / Crédit 1300.`
    )) return;
    try {
      const r = await api.post("/journal/od-periodiques/materialiser", { date_limite: dateLimite });
      toast.success(`${r.data.nb_materialisees} OD matérialisées.`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur matérialisation");
    }
  };

  const deletePending = async (od) => {
    if (!window.confirm(`Supprimer l'OD pending du ${od.date} (${od.montant} CHF) ?`)) return;
    try {
      await api.delete(`/journal/od-periodiques/${od.id}`);
      toast.success("OD supprimée");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const totalPending = items.filter((o) => o.statut === "pending").reduce((s, o) => s + o.montant, 0);
  const totalRealized = items.filter((o) => o.statut === "realized").reduce((s, o) => s + o.montant, 0);

  return (
    <div data-testid="od-periodiques-tab" className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" /> Comptabilité V2 transitoire
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              Matérialisation mensuelle des charges payées d'avance (compte 1300 « Actifs transitoires »)
              conforme CO art. 957. Activez la fonction sur une facture fournisseur depuis son détail
              (section « Répartition analytique »).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Globalement :</span>
            {globalActif === null ? (
              <Badge variant="outline">…</Badge>
            ) : globalActif ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Activée</Badge>
            ) : (
              <Badge variant="outline" className="text-gray-500">Désactivée</Badge>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={toggleGlobal} data-testid="od-toggle-global">
                {globalActif ? "Désactiver" : "Activer"}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-end gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Date limite de matérialisation</Label>
            <Input type="date" value={dateLimite}
              onChange={(e) => setDateLimite(e.target.value)} data-testid="od-date-limite" />
            <p className="text-[10px] text-gray-500 mt-0.5">
              Tous les OD pending datés ≤ cette date seront convertis en écritures.
            </p>
          </div>
          {canEdit && (
            <Button onClick={materialiser} disabled={!globalActif}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="od-materialiser">
              <Check className="w-4 h-4 mr-1.5" /> Matérialiser jusqu'au {dateLimite}
            </Button>
          )}
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Filtrer par statut</Label>
            <select value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
              data-testid="od-filter-statut">
              <option value="">Tous</option>
              <option value="pending">Pending</option>
              <option value="realized">Réalisées</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <Card className="p-3 bg-amber-50 border-amber-200">
            <p className="text-[10px] text-amber-700 uppercase">Pending</p>
            <p className="text-lg font-bold text-amber-900" data-testid="od-stat-pending-count">
              {items.filter((o) => o.statut === "pending").length}
            </p>
          </Card>
          <Card className="p-3 bg-amber-50 border-amber-200">
            <p className="text-[10px] text-amber-700 uppercase">Total pending</p>
            <p className="text-lg font-bold text-amber-900">{fmtChf(totalPending)} CHF</p>
          </Card>
          <Card className="p-3 bg-emerald-50 border-emerald-200">
            <p className="text-[10px] text-emerald-700 uppercase">Réalisées</p>
            <p className="text-lg font-bold text-emerald-900" data-testid="od-stat-realized-count">
              {items.filter((o) => o.statut === "realized").length}
            </p>
          </Card>
          <Card className="p-3 bg-emerald-50 border-emerald-200">
            <p className="text-[10px] text-emerald-700 uppercase">Total réalisées</p>
            <p className="text-lg font-bold text-emerald-900">{fmtChf(totalRealized)} CHF</p>
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            Aucune OD périodique. Activez la V2 sur une facture fournisseur avec répartition analytique.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="od-table">
              <thead className="text-xs text-gray-500 uppercase border-b">
                <tr>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Mois</th>
                  <th className="text-left py-2 px-2">Fournisseur</th>
                  <th className="text-left py-2 px-2">Facture</th>
                  <th className="text-right py-2 px-2">Montant</th>
                  <th className="text-center py-2 px-2">Statut</th>
                  <th className="text-center py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className="border-b hover:bg-gray-50" data-testid={`od-row-${o.id}`}>
                    <td className="py-2 px-2 font-mono text-xs">{o.date}</td>
                    <td className="py-2 px-2 text-xs">{o.mois}</td>
                    <td className="py-2 px-2 text-xs">{o.fournisseur_nom || "—"}</td>
                    <td className="py-2 px-2 text-xs">{o.facture_numero || "—"}</td>
                    <td className="py-2 px-2 text-right font-mono">{fmtChf(o.montant)}</td>
                    <td className="py-2 px-2 text-center">
                      {o.statut === "pending" ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">Pending</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">Réalisée</Badge>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {canEdit && o.statut === "pending" && (
                        <Button size="sm" variant="ghost" onClick={() => deletePending(o)}
                          className="text-red-600 h-7 w-7 p-0" data-testid={`od-delete-${o.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}


export default function Comptabilite() {
  // Get current user role for cloture permission gate
  const [role, setRole] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setRole(data.role)).catch(() => {});
  }, []);

  // iter105 — Tab contrôlé par URL pour les deep links (depuis audit CAMT)
  // ex. /comptabilite?tab=import-bancaire&import_id=X&focus_provisoire=Y
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return t || "journal";
    } catch {
      return "journal";
    }
  });

  const handleTabChange = (v) => {
    setActiveTab(v);
    // Met à jour l'URL sans recharger (préserve les autres query params)
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", v);
      // Si on quitte l'import-bancaire, on nettoie les params spécifiques
      if (v !== "import-bancaire") {
        url.searchParams.delete("import_id");
        url.searchParams.delete("focus_provisoire");
      }
      window.history.replaceState({}, "", url.toString());
    } catch { /* no-op */ }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="comptabilite-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-1">
        <BookOpen className="w-7 h-7 text-[#52a675]" /> Livre comptable
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Journal des écritures · plan comptable · balance · TVA · clôture
      </p>
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full overflow-x-auto justify-start no-scrollbar flex-nowrap">
          <TabsTrigger value="journal" data-testid="tab-journal"><FileText className="w-4 h-4 mr-2" /> Journal</TabsTrigger>
          <TabsTrigger value="balance" data-testid="tab-balance"><Scale className="w-4 h-4 mr-2" /> Balance</TabsTrigger>
          <TabsTrigger value="bilan" data-testid="tab-bilan"><Scale className="w-4 h-4 mr-2" /> Bilan</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="tab-pnl"><TrendingUp className="w-4 h-4 mr-2" /> Compte de résultat</TabsTrigger>
          <TabsTrigger value="tva" data-testid="tab-tva"><TrendingUp className="w-4 h-4 mr-2" /> TVA / AFC</TabsTrigger>
          <TabsTrigger value="controles" data-testid="tab-controles"><ShieldCheck className="w-4 h-4 mr-2" /> Contrôles</TabsTrigger>
          <TabsTrigger value="cloture" data-testid="tab-cloture"><Lock className="w-4 h-4 mr-2" /> Clôture</TabsTrigger>
          <TabsTrigger value="import-bancaire" data-testid="tab-import-bancaire"><Upload className="w-4 h-4 mr-2" /> Import bancaire</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan"><Calculator className="w-4 h-4 mr-2" /> Plan comptable</TabsTrigger>
          <TabsTrigger value="od-periodiques" data-testid="tab-od-periodiques"><Calendar className="w-4 h-4 mr-2" /> OD périodiques</TabsTrigger>
        </TabsList>
        <TabsContent value="journal" className="mt-4"><JournalTab /></TabsContent>
        <TabsContent value="balance" className="mt-4"><BalanceTab /></TabsContent>
        <TabsContent value="bilan" className="mt-4"><BilanTab /></TabsContent>
        <TabsContent value="pnl" className="mt-4"><CompteResultatTab /></TabsContent>
        <TabsContent value="tva" className="mt-4"><TvaDecompteTab role={role} /></TabsContent>
        <TabsContent value="controles" className="mt-4"><ControlesTab /></TabsContent>
        <TabsContent value="cloture" className="mt-4"><ClotureTab role={role} /></TabsContent>
        <TabsContent value="import-bancaire" className="mt-4"><ImportBancaireTab /></TabsContent>
        <TabsContent value="plan" className="mt-4"><PlanComptesTab /></TabsContent>
        <TabsContent value="od-periodiques" className="mt-4"><OdPeriodiquesTab role={role} /></TabsContent>
      </Tabs>
    </div>
  );
}
